var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useRef, useState } from "react";
import moment from "moment";
import useErrors from "../Hooks/useErrors";
import useCachedLoadCurrentPersonInfo from "../Hooks/useCachedLoadCurrentPersonInfo";
import useService from "../Hooks/useService";
import TaskService from "../../Services/Task.service";
import { TaskStatuses } from "../../../components/shared/domainTypes/task/task.type";
import Widget from "../UI/Widget/Widget";
import WidgetHeader from "../UI/Widget/WidgetHeader";
import WidgetRefreshButton from "../Presentational/Widgets/WidgetRefreshButton";
import ErrorList from "../UI/ErrorDetail/ErrorList";
import TaskDetailModal from "../Presentational/Task/TaskDetailModal";
import CaseloadTasksFilterModal from "./CaseloadTasksFilterModal";
import ServiceResult from "../UI/ServiceResult/ServiceResult";
import TaskStatusBadge from "../Presentational/TaskStatus/TaskStatusBadge";
import TaskUpdateModal from "../Presentational/Task/TaskUpdateModal";
import TaskPriorityBadge from "../Presentational/TaskPriority/TaskPriorityBadge";
import CompleteButton from "../UI/CompleteButton/CompleteButton";
import { useVirtualizedApp } from "../Hooks/useVirtualizedApp";
import LinkToPerson from "../Presentational/Person/LinkToPerson/LinkToPerson";
import useConfiguration from "../Hooks/useConfiguration";
const CaseloadTasksContainer = () => {
    const PAGE_SIZE = 15;
    const [errors, addError, clearErrors] = useErrors();
    const getCurrentPerson = useCachedLoadCurrentPersonInfo();
    const currentPerson = useRef();
    const [successMessage, setSuccessMessage] = React.useState("");
    const [loadTasksState, loadTasks, taskActions] = useService(TaskService.getTasksAboutPeopleOnMyCaseload);
    const [taskIdInUpdateModal, setTaskIdInUpdateModal] = React.useState(null);
    const [updateModalIsOpen, setUpdateModalIsOpen] = React.useState(false);
    const [pageIndex, setPageIndex] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [isLoadingPage, setIsLoadingPage] = useState(false);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [filters, setFilters] = useState({
        dueInDaysEnabled: false,
        dueInDaysNumber: "",
        overdueHidden: false,
        withoutDueDateHidden: false,
    });
    const virtualizedAppContext = useVirtualizedApp();
    const [isTaskDetailModalOpen, setIsTaskDetailModalOpen] = React.useState(false);
    const [taskDetailsTaskId, setTaskDetailsTaskId] = React.useState(null);
    const taskInDetailModal = React.useMemo(() => loadTasksState.data &&
        loadTasksState.data.find((t) => t.id === taskDetailsTaskId), [loadTasksState.data, taskDetailsTaskId]);
    const taskInUpdateModal = React.useMemo(() => loadTasksState.data &&
        loadTasksState.data.find((t) => t.id === taskIdInUpdateModal), [loadTasksState.data, taskIdInUpdateModal]);
    const [taskMarkingComplete, setTaskMarkingComplete] = React.useState(0);
    const loadTaskData = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!currentPerson.current) {
            // @ts-expect-error - TS2322 - Type 'PersonInfo' is not assignable to type 'undefined'.
            currentPerson.current = yield getCurrentPerson();
        }
        const xFormedFilters = {
            overdueHidden: filters.overdueHidden,
            withoutDueDateHidden: filters.withoutDueDateHidden,
            dueBefore: null,
        };
        if (filters.dueInDaysEnabled && filters.dueInDaysNumber !== "") {
            const futureDate = new Date();
            futureDate.setDate(
            // @ts-expect-error - TS2345 - Argument of type 'string | number' is not assignable to parameter of type 'string'.
            futureDate.getDate() + parseInt(filters.dueInDaysNumber, 10));
            xFormedFilters.dueBefore = futureDate;
        }
        const tasks = yield loadTasks(xFormedFilters, pageIndex * PAGE_SIZE, PAGE_SIZE + 1);
        if (tasks && tasks.length > PAGE_SIZE) {
            setHasNextPage(true);
        }
        else {
            setHasNextPage(false);
        }
        setIsLoadingPage(false);
    }), [filters, getCurrentPerson, loadTasks, pageIndex]);
    const isTaskOverDue = (task) => {
        if (!task.dueDate) {
            return false;
        }
        // If it's completed or cancelled, it can't be overdue.
        if (task.status.id === TaskStatuses.completed.id ||
            task.status.id === TaskStatuses.cancelled.id) {
            return false;
        }
        // If the due date is before now, it's overdue
        if (moment(task.dueDate).isBefore(moment())) {
            return true;
        }
        return false;
    };
    // @ts-expect-error - TS7006 - Parameter 'task' implicitly has an 'any' type.
    function openTaskModal(task) {
        // set the task detali id and open the modal and all that jazz.
        setIsTaskDetailModalOpen(true);
        setTaskDetailsTaskId(task.id);
    }
    function showSuccessMessage(message) {
        setSuccessMessage(message);
        // After a certain amount of time, hide the success message.
        setTimeout(() => {
            setSuccessMessage("");
        }, 5000);
    }
    // @ts-expect-error - TS7006 - Parameter 'task' implicitly has an 'any' type.
    function taskCheckBoxPress(task) {
        return __awaiter(this, void 0, void 0, function* () {
            const IN_PROGRESS_STATUS_ID = 2;
            const COMPLETE_STATUS_ID = 3;
            const newStatus = task.status.id === COMPLETE_STATUS_ID
                ? IN_PROGRESS_STATUS_ID
                : COMPLETE_STATUS_ID;
            setTaskMarkingComplete(task.id);
            try {
                yield TaskService.updateTaskStatus(task.id, newStatus);
                // Once the update is complete, then we need to refresh the task list.
                loadTaskData();
                const statusName = newStatus === COMPLETE_STATUS_ID ? "complete" : "incomplete";
                showSuccessMessage(`Task "${task.title}" marked ${statusName}`);
            }
            catch (error) {
                // @ts-expect-error - TS2349 - This expression is not callable.
                addError("Error updating task status.", error.toString());
            }
            finally {
                setTaskMarkingComplete(0);
            }
        });
    }
    function prevPage() {
        setIsLoadingPage(true);
        setPageIndex((o) => Math.max(o - 1, 0));
    }
    function nextPage() {
        setIsLoadingPage(true);
        setPageIndex((o) => o + 1);
    }
    function openFilterModal() {
        setIsFilterModalOpen(true);
    }
    function closeFilterModal() {
        setIsFilterModalOpen(false);
    }
    function isFiltered() {
        return !!Object.values(filters).find((value) => typeof value === "boolean" && value);
    }
    React.useEffect(() => {
        loadTaskData();
    }, [loadTaskData]);
    const personTypeImportEnabledConfiguration = useConfiguration("personTypeImportEnabled");
    return (_jsx(_Fragment, { children: _jsxs(Widget, { children: [_jsx(WidgetHeader, { title: "Student Caseload Tasks", subtitle: {
                        content: personTypeImportEnabledConfiguration
                            ? ""
                            : "This widget only shows tasks for your student caseload",
                    }, children: _jsxs("div", { className: "d-flex", children: [_jsxs("button", { type: "button", className: "btn btn-link py-0", onClick: () => {
                                    openFilterModal();
                                }, title: "Filter", "aria-label": "Filter", children: [_jsx("span", { className: "fal fa-filter", "aria-hidden": "true", title: "Filter" }), _jsx("span", { className: "sr-only", children: "Filter" }), isFiltered() && (_jsx("span", { className: "fas fa-circle text-danger", "aria-hidden": "true", style: {
                                            verticalAlign: "super",
                                            fontSize: "0.5rem",
                                            marginLeft: "2px",
                                        } }))] }), _jsx(WidgetRefreshButton, { isLoading: loadTasksState.isLoading, onRefresh: () => {
                                    loadTaskData();
                                } })] }) }), successMessage && (_jsx("div", { className: "px-3", children: _jsxs("div", { className: "alert alert-success", children: [_jsx("span", { className: "fa fa-check-circle", "aria-hidden": "true" }), " ", successMessage] }) })), _jsx(ErrorList, { errors: errors, onClear: clearErrors }), _jsx(ServiceResult, { state: loadTasksState, errorClassName: "m-3", errorMessage: "Error loading tasks", children: (tasks) => (_jsxs("div", { className: "list-group list-group-flush", children: [tasks.length ? (tasks.slice(0, PAGE_SIZE).map((task) => {
                                var _a, _b;
                                return (_jsxs("div", { className: `list-group-item list-group-item-action ${task.isPrivate ? "bg-light" : ""}`, children: [" ", _jsxs("div", { className: "d-flex", children: [_jsx("div", { className: "d-flex align-items-start justify-content-center mr-2", children: _jsx(CompleteButton, { isComplete: task.status.id === TaskStatuses.completed.id, 
                                                        // @ts-expect-error - TS2322 - Type '{ isComplete: boolean; type: string; className: string; onClick: (event: any) => void; title: string; isLoading: boolean; }' is not assignable to type 'IntrinsicAttributes & Props'.
                                                        type: "button", className: "", 
                                                        // @ts-expect-error - TS7006 - Parameter 'event' implicitly has an 'any' type.
                                                        onClick: (event) => {
                                                            taskCheckBoxPress(task);
                                                            // Since the checkbox is inside another element that has an onclick handler, we need
                                                            // to call stopPropagation so both handlers aren't fired when the checkbox is clicked.
                                                            event.stopPropagation();
                                                        }, title: task.status.id === TaskStatuses.completed.id
                                                            ? "This task is complete"
                                                            : "Mark this task complete", isLoading: task.id === taskMarkingComplete }) }), _jsxs("div", { className: "d-flex flex-column w-100", children: [_jsxs("div", { className: "d-flex justify-content-between flex-wrap", children: [_jsx("div", { className: "d-flex flex-column justify-content-center", children: _jsxs("div", { className: "d-flex align-items-center", children: [task.isPrivate ? (_jsx("span", { className: "fa fa-lock mr-2", title: "This is a private task and can only be viewed by you." })) : (""), _jsx("button", { className: "btn btn-link p-0", onClick: () => {
                                                                                    openTaskModal(task);
                                                                                }, children: _jsx("h5", { className: "mb-0", children: task.title }) })] }) }), _jsx("div", { children: _jsx(TaskStatusBadge, { taskStatus: task.status, className: "clickable", onClick: () => {
                                                                            setTaskIdInUpdateModal(task.id);
                                                                            setUpdateModalIsOpen(true);
                                                                        } }) })] }), _jsxs("div", { className: "d-flex flex-column", children: [_jsx("div", { children: task.priority.id !== 2 && (_jsx(TaskPriorityBadge, { priority: task.priority })) }), _jsxs("div", { className: "d-flex", children: [task.startDate ? (_jsxs("div", { className: "d-flex align-items-center", children: [!task.dueDate ? (_jsx("small", { className: "mr-2", children: "Start " })) : (""), _jsx("small", { className: "mb-0", children: moment(task.startDate).format("ddd l") })] })) : (""), task.startDate && task.dueDate ? (_jsx("div", { className: "d-flex align-items-center", children: _jsx("small", { className: "mx-1 mb-1", children: "\u2013" }) })) : (""), task.dueDate ? (_jsxs("div", { className: `d-flex align-items-center ${isTaskOverDue(task) ? "text-danger" : ""}`, children: [!task.startDate ? (_jsx("small", { className: "mr-2", children: "Due " })) : (""), _jsx("small", { className: "mb-0", children: moment(task.dueDate).format("ddd l") })] })) : ("")] }), task.assignedPerson && (_jsxs("div", { className: "d-flex align-items-center", children: [_jsx("small", { children: "Assigned:\u00A0" }), _jsx(LinkToPerson, { className: "d-flex align-items-center", affiliationId: task.assignedPerson.affiliationId, tabId: "Tasks", virtualizedApp: virtualizedAppContext.name, children: _jsx("small", { children: (_a = task.assignedPerson) === null || _a === void 0 ? void 0 : _a.fullName }) })] })), _jsxs("div", { className: "d-flex align-items-center", children: [_jsx("small", { children: "About:\u00A0" }), _jsx(LinkToPerson, { className: "d-flex align-items-center", affiliationId: task.regardingPerson.affiliationId, tabId: "Tasks", virtualizedApp: virtualizedAppContext.name, children: _jsx("small", { children: (_b = task.regardingPerson) === null || _b === void 0 ? void 0 : _b.fullName }) })] })] })] })] })] }, task.id));
                            })) : (_jsx("div", { className: "list-group-item text-center", children: "There are no active tasks for people on your caseload." })), taskInUpdateModal && (_jsx(TaskUpdateModal, { size: "xl", task: taskInUpdateModal, isOpen: updateModalIsOpen, onModalClosed: () => setUpdateModalIsOpen(false), onUpdated: (task) => {
                                    taskActions.edit((ts) => 
                                    // @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
                                    ts.map((t) => (t.id === task.id ? task : t)));
                                } }))] })) }), pageIndex > 0 || hasNextPage ? (_jsx("div", { className: "card-body py-2", children: _jsxs("div", { className: "d-flex justify-content-end", children: [pageIndex > 0 && (_jsx("button", { className: `btn btn-outline-dark ${hasNextPage ? "mr-2" : ""}`, onClick: () => {
                                    prevPage();
                                }, title: "Previous Page", disabled: isLoadingPage, children: _jsx("span", { className: "fas fa-angle-left", "aria-hidden": "true" }) })), hasNextPage && (_jsx("button", { className: "btn btn-outline-dark", onClick: () => {
                                    nextPage();
                                }, title: "Next Page", disabled: isLoadingPage, children: _jsx("span", { className: "fas fa-angle-right", "aria-hidden": "true" }) }))] }) })) : (""), taskInDetailModal && (_jsx(TaskDetailModal, { taskOrTaskId: taskInDetailModal, isOpen: isTaskDetailModalOpen, onModalClosed: () => {
                        setIsTaskDetailModalOpen(false);
                    }, setTask: (task) => {
                        taskActions.edit((ts) => 
                        // @ts-expect-error - TS2533 - Object is possibly 'null' or 'undefined'.
                        ts.map((t) => (t.id === task.id ? task : t)));
                    } })), _jsx(CaseloadTasksFilterModal, { isOpen: isFilterModalOpen, onApplyFilters: (newFilters) => {
                        setFilters(newFilters);
                    }, onModalClosed: closeFilterModal })] }) }));
};
export default CaseloadTasksContainer;
